import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { orderBy } from 'natural-orderby';
import * as colorSchemes from 'd3-scale-chromatic';
import {
    getMinMaxValues,
    getNormalizationFunction,
} from '../../utils/heatmapFormatUtils';
import HeatmapLegend from './HeatmapLegend';
import { getFormattedValue } from '../../../dashboardUtils';

function getCellColorFunction(colorSchemeFunction) {
    return (value) => (value === 0 ? '#FFF' : colorSchemeFunction(value));
}

function renderRowCells(
    rowKey,
    cellValues,
    getCellColor,
    getNormalizedValue,
    suffix
) {
    let count = 0;

    return cellValues.map((cellValue) => {
        count += 1;
        const cellStyles = {
            background: getCellColor(getNormalizedValue(cellValue)),
        };

        return (
            <td
                key={`${rowKey}${count}`}
                title={getFormattedValue(cellValue, suffix)}
                className="heatmap-table__cell"
                style={cellStyles}
            />
        );
    });
}

function renderRows(formattedData, getCellColor, getNormalizedValue, suffix) {
    const rowKeys = orderBy(Object.keys(formattedData));

    return rowKeys.map((rowKey) => (
        <tr key={rowKey}>
            <td className="heatmap-table__cell">{rowKey}</td>
            {renderRowCells(
                rowKey,
                formattedData[rowKey],
                getCellColor,
                getNormalizedValue,
                suffix
            )}
        </tr>
    ));
}

function renderHeaders(bucketTimes, timeAxisFormatter) {
    return (
        <tr>
            <th className="heatmap-table__header" aria-label="heatmap-corner" />
            {bucketTimes.map((bucketTime) => (
                <th key={bucketTime} className="heatmap-table__header">
                    <div>
                        <span>{timeAxisFormatter(bucketTime)}</span>
                    </div>
                    <div />
                </th>
            ))}
        </tr>
    );
}

function TimeSeriesHeatmap(props) {
    const heatmapRef = useRef(null);
    const [legendHeight, setLegendHeight] = useState(0);
    const [legendOffsetTop, setLegendOffsetTop] = useState(0);

    useEffect(() => {
        if (heatmapRef.current) {
            setLegendHeight(heatmapRef.current.clientHeight);
            setLegendOffsetTop(heatmapRef.current.offsetTop);
        }
    }, []);

    const { min, max } = getMinMaxValues(
        props.formattedData,
        props.configuration.valueRange
    );

    const getNormalizedValue = getNormalizationFunction(min, max);
    const getCellColor = getCellColorFunction(
        colorSchemes[props.configuration.colorScheme]
    );

    if (isEmpty(props.formattedData)) {
        return null;
    }

    return (
        <div className="heatmap">
            <div className="heatmap-container">
                <table className="heatmap-table">
                    <thead>
                        {renderHeaders(
                            props.bucketTimes,
                            props.timeAxisFormatter
                        )}
                    </thead>
                    <tbody ref={heatmapRef}>
                        {renderRows(
                            props.formattedData,
                            getCellColor,
                            getNormalizedValue,
                            props.configuration.suffix
                        )}
                    </tbody>
                </table>
                {props.configuration.showLegend && (
                    <HeatmapLegend
                        getCellColor={getCellColor}
                        getNormalizedValue={getNormalizedValue}
                        max={max}
                        min={min}
                        legendHeight={legendHeight}
                        legendOffsetTop={legendOffsetTop}
                        suffix={props.configuration.suffix}
                    />
                )}
            </div>
        </div>
    );
}

TimeSeriesHeatmap.propTypes = {
    bucketTimes: PropTypes.instanceOf(Array).isRequired,
    configuration: PropTypes.instanceOf(Object).isRequired,
    formattedData: PropTypes.instanceOf(Object).isRequired,
    timeAxisFormatter: PropTypes.func.isRequired,
};

export default React.memo(TimeSeriesHeatmap, (prevProps, nextProps) =>
    isEqual(prevProps.formattedData, nextProps.formattedData)
);
